/**
 * A recipe's ingredients and steps, in plain words (section 4). Ingredients scale with the
 * servings viewers pick unless switched off; each step may have a timer and a clip in the video
 * (cook mode loops it while the step is shown). The clip is a step moment the step points at;
 * every id is made here, never typed.
 */

import Button from "@web-ui/components/Button";
import Checkbox from "@web-ui/components/Checkbox";
import Field from "@web-ui/components/Field";
import { moveItem } from "@/screens/rows";
import { useForm } from "../form-context";
import { nextId, setStepClip, type Marker } from "../moments";
import { fieldDomId, getPath } from "../paths";
import { ClockField, Group, LocalizedField } from "../controls/inputs";
import { RowActions } from "../controls/picker";

interface Ingredient { id: string; name?: Record<string, string>; quantity?: number; unit?: string; scalable?: boolean; group?: Record<string, string> }
interface Step { id: string; text?: Record<string, string>; markerId?: string; timerSeconds?: number }

export function RecipeSection() {
  const form = useForm();
  const ingredients = (getPath(form.doc, "recipe.ingredients") as Ingredient[] | undefined) ?? [];
  const steps = (getPath(form.doc, "recipe.steps") as Step[] | undefined) ?? [];
  const markers = (getPath(form.doc, "playback.markers") as Marker[] | undefined) ?? [];
  const hasVideo = !!getPath(form.doc, "playback.video.videoId");
  const setIngredients = (next: Ingredient[]) => form.set("recipe.ingredients", next, true);
  const setSteps = (next: Step[]) => form.set("recipe.steps", next, true);

  return (
    <div className="flex flex-col gap-6">
      <Group path="recipe.ingredients" label="Ingredients" optional>
        {ingredients.length ? (
          <ol className="flex flex-col gap-2">
            {ingredients.map((ing, i) => (
              <li key={ing.id} className="grid items-start gap-2 rounded-2xl bg-b-surface1 p-3 md:grid-cols-[6rem_7rem_minmax(0,1fr)_auto]">
                <Field id={fieldDomId(`recipe.ingredients.${i}.quantity`)} label="Amount" size="sm" type="number" min={0} step="any" optional value={ing.quantity === undefined ? "" : String(ing.quantity)} error={form.issue(`recipe.ingredients.${i}.quantity`)?.message} disabled={!form.canEdit}
                  onChange={(e) => form.set(`recipe.ingredients.${i}.quantity`, e.target.value === "" ? undefined : Number(e.target.value))} onBlur={() => form.touch(`recipe.ingredients.${i}.quantity`)} />
                <Field label="Unit" size="sm" optional placeholder="g, ml, tbsp" value={ing.unit ?? ""} disabled={!form.canEdit} onChange={(e) => form.set(`recipe.ingredients.${i}.unit`, e.target.value)} />
                <div className="flex flex-col gap-2">
                  <LocalizedField path={`recipe.ingredients.${i}.name`} label="Ingredient" size="sm" required />
                  <Checkbox label="Scales with the servings" checked={ing.scalable !== false} disabled={!form.canEdit} onChange={(on) => form.set(`recipe.ingredients.${i}.scalable`, on ? undefined : false)} />
                </div>
                {form.canEdit ? <div className="flex gap-1 md:mt-7"><RowActions name={ing.name?.[form.locale] || `ingredient ${i + 1}`} index={i} count={ingredients.length} onMove={(to) => setIngredients(moveItem(ingredients, i, to))} onRemove={() => setIngredients(ingredients.filter((_, j) => j !== i))} /></div> : null}
              </li>
            ))}
          </ol>
        ) : <p className="text-body-2 text-t-secondary">No ingredients yet.</p>}
        {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setIngredients([...ingredients, { id: nextId("ingredient", ingredients.map((x) => x.id)) }])}>Add an ingredient</Button></div> : null}
      </Group>

      <Group path="recipe.steps" label="Steps" optional help="Cook mode shows one step at a time. Give a step a clip and the TV loops that part of the video while it is shown.">
        {steps.length ? (
          <ol className="flex flex-col gap-2">
            {steps.map((step, i) => {
              const clip = markers.find((m) => m.id === step.markerId);
              const setClip = (start: number | undefined, end: number | undefined) => {
                const out = setStepClip(markers, step, start === undefined ? null : { start, end });
                form.update((d) => {
                  const playback = { ...((d["playback"] as Record<string, unknown>) ?? {}) };
                  if (out.markers.length) playback["markers"] = out.markers; else delete playback["markers"];
                  const recipe = { ...(d["recipe"] as Record<string, unknown>) };
                  recipe["steps"] = steps.map((s, j) => (j === i ? { ...s, markerId: out.markerId } : s)).map((s) => (s.markerId ? s : (({ markerId: _m, ...rest }) => rest)(s)));
                  return { ...d, playback, recipe };
                });
              };
              return (
                <li key={step.id} className="flex flex-col gap-3 rounded-2xl bg-b-surface1 p-3">
                  <div className="flex items-center gap-2">
                    <span className="grow text-sub-title-2">{`Step ${i + 1}`}</span>
                    {form.canEdit ? <RowActions name={`step ${i + 1}`} index={i} count={steps.length} onMove={(to) => setSteps(moveItem(steps, i, to))} onRemove={() => { setClip(undefined, undefined); setSteps(steps.filter((_, j) => j !== i)); }} /> : null}
                  </div>
                  <LocalizedField path={`recipe.steps.${i}.text`} label="What to do" multiline required />
                  <div className="grid gap-2 sm:grid-cols-3">
                    <ClockField label="Timer" optional value={step.timerSeconds} onChange={(v) => form.set(`recipe.steps.${i}.timerSeconds`, v && v > 0 ? Math.round(v) : undefined)} />
                    {hasVideo ? <>
                      <ClockField label="Clip from" optional value={clip?.startSeconds} onChange={(v) => setClip(v, clip?.endSeconds)} />
                      <ClockField label="Clip to" optional value={clip?.endSeconds} onChange={(v) => clip ? setClip(clip.startSeconds, v) : undefined} />
                    </> : <p className="self-end text-caption text-t-secondary sm:col-span-2">Add the video to give this step a clip.</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        ) : <p className="text-body-2 text-t-secondary">No steps yet.</p>}
        {form.canEdit ? <div><Button type="button" size="sm" variant="secondary" icon="plus" onClick={() => setSteps([...steps, { id: nextId("step", steps.map((x) => x.id)) }])}>Add a step</Button></div> : null}
      </Group>
    </div>
  );
}
